import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import QuizProgress from "./QuizProgress";
import QuizQuestion from "./QuizQuestion";
import QuizResult from "./QuizResult";
import QuizIntro, { IntroData } from "./QuizIntro";
import QuizLeadForm, { LeadFormData, QuizPrefillData } from "./QuizLeadForm";
import { supabase } from "@/integrations/supabase/client";
import {
  quizQuestions,
  calculateScore,
  calculateBlockScores,
  getProfile,
  BlockScores,
  QuizProfile,
} from "./quizData";

interface SupplierQuizProps {
  isOpen: boolean;
  onClose: () => void;
  onScheduleMeeting?: (prefillData: QuizPrefillData) => void;
}

type QuizStep = "intro" | "questions" | "result" | "leadForm";

const SupplierQuiz = ({ isOpen, onClose, onScheduleMeeting }: SupplierQuizProps) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [step, setStep] = useState<QuizStep>("intro");
  const [introData, setIntroData] = useState<IntroData | null>(null);
  const [result, setResult] = useState<{
    totalScore: number;
    blockScores: BlockScores;
    profile: QuizProfile;
  } | null>(null);
  const prospectIdRef = useRef<string | null>(null);

  // Upsert prospect data progressively
  const upsertProspect = async (data: Record<string, unknown>) => {
    try {
      if (prospectIdRef.current) {
        await supabase.functions.invoke("upsert-prospect", {
          body: { id: prospectIdRef.current, ...data },
        });
      } else {
        const { data: result } = await supabase.functions.invoke("upsert-prospect", {
          body: data,
        });
        if (result?.prospectId) {
          prospectIdRef.current = result.prospectId;
        }
      }
    } catch (err) {
      console.error("Prospect upsert error:", err);
    }
  };

  const handleAnswer = (value: number) => {
    setAnswers((prev) => ({ ...prev, [currentQuestion]: value }));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    } else {
      const totalScore = calculateScore(answers);
      const blockScores = calculateBlockScores(answers);
      const profile = getProfile(totalScore);
      setResult({ totalScore, blockScores, profile });
      setStep("result");

      // Save quiz results to prospect
      if (introData) {
        upsertProspect({
          fullName: introData.name,
          phone: introData.whatsapp,
          source: "quiz",
          quizAnswers: answers,
          quizTotalScore: totalScore,
          quizBlockScores: blockScores,
          quizProfileId: profile.id,
          quizProfileTitle: profile.title,
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  const handleIntroComplete = (data: IntroData) => {
    setIntroData(data);
    setStep("questions");

    // Save name + whatsapp to prospects immediately
    upsertProspect({
      fullName: data.name,
      phone: data.whatsapp,
      source: "quiz",
    });
  };

  const handleContinueToLead = () => {
    setStep("leadForm");
  };

  const handleLeadSubmit = (_data: LeadFormData) => {
    // Data is now saved via edge function in QuizLeadForm
  };

  const handleScheduleMeeting = (prefillData: QuizPrefillData) => {
    onScheduleMeeting?.(prefillData);
    handleClose();
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setCurrentQuestion(0);
      setAnswers({});
      setStep("intro");
      setIntroData(null);
      setResult(null);
      prospectIdRef.current = null;
    }, 300);
  };

  const canGoNext = answers[currentQuestion] !== undefined;

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        side="bottom"
        className="h-[90vh] sm:h-[85vh] rounded-t-3xl overflow-hidden p-0"
      >
        <div className="flex flex-col h-full">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-xl font-bold text-brand-dark">
                {step === "intro" && "Diagnóstico do seu Negócio"}
                {step === "questions" && "Diagnóstico do seu Negócio"}
                {step === "result" && "Seu Resultado"}
                {step === "leadForm" && "Salvar Diagnóstico"}
              </SheetTitle>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="rounded-full"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            <AnimatePresence mode="wait">
              {step === "intro" && (
                <motion.div key="intro" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <QuizIntro onStart={handleIntroComplete} />
                </motion.div>
              )}

              {step === "questions" && (
                <motion.div key="questions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <QuizProgress current={currentQuestion} total={quizQuestions.length} />
                  <QuizQuestion
                    key={currentQuestion}
                    question={quizQuestions[currentQuestion]}
                    selectedValue={answers[currentQuestion]}
                    onSelect={handleAnswer}
                  />
                </motion.div>
              )}

              {step === "result" && result && (
                <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <QuizResult
                    totalScore={result.totalScore}
                    blockScores={result.blockScores}
                    profile={result.profile}
                    onContinue={handleContinueToLead}
                  />
                </motion.div>
              )}

              {step === "leadForm" && result && introData && (
                <motion.div key="leadForm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <QuizLeadForm
                    profile={result.profile}
                    totalScore={result.totalScore}
                    blockScores={result.blockScores}
                    answers={answers}
                    name={introData.name}
                    whatsapp={introData.whatsapp}
                    prospectId={prospectIdRef.current}
                    onSubmit={handleLeadSubmit}
                    onScheduleMeeting={handleScheduleMeeting}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {step === "questions" && (
            <div className="px-6 py-4 border-t border-border bg-background shrink-0">
              <div className="flex justify-between items-center max-w-md mx-auto">
                <Button variant="ghost" onClick={handlePrevious} disabled={currentQuestion === 0} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  Anterior
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!canGoNext}
                  className="gap-2 bg-brand-orange hover:bg-brand-orange/90 text-white"
                >
                  {currentQuestion === quizQuestions.length - 1 ? "Ver Resultado" : (
                    <>
                      Próxima
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default SupplierQuiz;
